<script setup lang="ts">
import type { AdminUserListItem } from "@simbelapp/order-sdk";
import { storeToRefs } from "pinia";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import ConfirmDeleteModal from "~/components/Common/Modals/ConfirmDeleteModal.vue";
import RegisterPasswordBase from "~/components/Pages/Auth/steps/RegisterPasswordBase.vue";
import { useAuth } from "~/composables/auth/useAuth";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useAuthStore } from "~/store/auth/auth.store";
import { useUsersStore } from "~/store/users/users.store";

const props = defineProps<{ user: AdminUserListItem }>();
const emit = defineEmits<{ hide: []; done: [] }>();

const auth = useAuth();
const authStore = useAuthStore();
const feedback = useFeedback();
const usersStore = useUsersStore();
const { connexion } = storeToRefs(authStore);
const loading = ref(false);
const showConfirmation = ref(false);
const pendingPassword = ref<string | null>(null);
const passwordForm = ref<InstanceType<typeof RegisterPasswordBase> | null>(null);

function clearPasswordFields() {
  authStore.clearRegistrationPasswordFields();
  pendingPassword.value = null;
  showConfirmation.value = false;
  passwordForm.value?.resetValidation();
}

function closeModal() {
  clearPasswordFields();
  emit("hide");
}

async function requestConfirmation() {
  const isValid = await passwordForm.value?.validate();
  const password = connexion.value.registerPasswordFirst;

  if (!isValid || !password) {
    return;
  }

  pendingPassword.value = auth.hashPassword(password);
  showConfirmation.value = true;
}

function cancelConfirmation() {
  pendingPassword.value = null;
  showConfirmation.value = false;
}

async function confirmReset() {
  if (!pendingPassword.value) return;

  loading.value = true;

  try {
    await usersStore.resetPasswordAsSimbelAdmin(props.user.userId, pendingPassword.value);
    feedback.success("Le mot de passe a bien été réinitialisé", "small");
    clearPasswordFields();
    emit("done");
  } catch {
    // The store reports the error; keep confirmation open for retry.
  } finally {
    loading.value = false;
  }
}

clearPasswordFields();
onUnmounted(clearPasswordFields);
</script>

<template>
  <CenterModal v-if="!showConfirmation" class="w-full max-w-[488px]" @hide="closeModal">
    <template #body>
      <form class="flex flex-col gap-6 p-6" @submit.prevent="requestConfirmation">
        <div class="flex flex-col gap-2">
          <h3 class="heading_3_demi text-primary-color">Réinitialiser le mot de passe</h3>
          <p class="body_regular text-secondary-color">
            Définissez un nouveau mot de passe pour {{ props.user.email ?? props.user.fullname }}.
          </p>
        </div>

        <RegisterPasswordBase ref="passwordForm" />

        <div class="flex justify-end gap-4 border-t border-stroke-color pt-4">
          <AppButton type="outlined" label="Annuler" @click="closeModal" />
          <AppButton label="Continuer" native-type="submit" />
        </div>
      </form>
    </template>
  </CenterModal>
  <ConfirmDeleteModal
    v-if="showConfirmation"
    title="Confirmer la réinitialisation du mot de passe ?"
    :description="`Le mot de passe de ${props.user.email ?? props.user.fullname} sera remplacé immédiatement.`"
    :consequences="[]"
    confirm-text="Confirmer"
    :loading="loading"
    @cancel="cancelConfirmation"
    @confirm="confirmReset"
  />
</template>
